import type { RegistrationAuthority } from "@/utils/registryAccounts";

/** Only an RGP transaction carries registry data: the PLN unit price the registry records. */
export interface RegistrationAuthorityData {
  unit_price_pln: string;
}

/** Sent on every transaction save, spot or delivery, always as a value or an explicit null. */
export interface SharedTransactionFields {
  counterparty_registration_authority: RegistrationAuthority;
  registration_authority_data: RegistrationAuthorityData | null;
  swap_referrer_company_id: number | null;
}

/**
 * The delivery-editable inputs as the transaction page holds them; a blank input is `""`.
 *
 * @property tradeOwnerCompanyId - The trade owner as loaded, echoed back: the deal owns it, and the
 *   page's swap-margin rule would otherwise clear one the deal set before any producer price existed
 */
export interface DeliveryFormValues {
  counterpartyEecsAccountNumber: string;
  excludeReferralCommission: boolean;
  tradeOwnerCompanyId: number | null;
}

/** The PUT body of a forwards v2 delivery; a blank account number is left out ("no change"). */
export interface DeliveryUpdatePayload extends SharedTransactionFields {
  counterparty_eecs_account_number?: string;
  exclude_referral_commission: boolean;
}

/**
 * Builds the save of a forwards v2 delivery from the only fields it may send.
 *
 * The buyer, contract and price fields belong to the delivery's deal, which syncs them onto every
 * unshipped delivery, and the backend refuses a change to them here (RV2-delivery-escrow-moves-01).
 * Leaving them out — along with `global_company_id` and the per-producer `item_prices` the deal's
 * page sets — keeps an unpriced delivery (whose price loads as the 0 default) from being refused
 * for a field nobody touched.
 *
 * The trade owner is deal-owned as well, so the loaded one is echoed in place of the page's own.
 *
 * The account number is deal-owned and read-only too, yet it is echoed back: the backend
 * re-derives counterparty_registry_country from it plus the authority, and deriving it from a
 * missing number would blank a deal-owned column — which is exactly what the fence refuses.
 */
export function buildDeliveryPayload(
  form: DeliveryFormValues,
  shared: SharedTransactionFields
): DeliveryUpdatePayload {
  return {
    ...(isBlank(form.counterpartyEecsAccountNumber)
      ? {}
      : {
          counterparty_eecs_account_number: form.counterpartyEecsAccountNumber,
        }),
    exclude_referral_commission: form.excludeReferralCommission,
    ...shared,
    swap_referrer_company_id: form.tradeOwnerCompanyId,
  };
}

/** A save omits a blank field rather than sending it, so the backend reads it as "no change". */
export function isBlank(value: unknown): value is "" | null | undefined {
  return value === "" || value === null || value === undefined;
}
